/**
 * LIBRARY interior — pure layout + graph math (no PlayCanvas, unit-testable).
 * Room dimensions, bookcase-stack geometry, book shelving, spine colours, the
 * knowledge-graph edge set and map-table pin positions. The rendering system
 * in library.ts composes these.
 */
import type { KnowledgeEntry } from '../../api/types'
import type { AABB } from '../colliders'

// ── Room dimensions (island-local, origin-centred) ─────────────────────────

/** Half-width (X). Room is 24 m across. */
export const LIBRARY_HALF_X = 12
/** Half-depth (Z). Room is 26 m deep. */
export const LIBRARY_HALF_Z = 13
/** A library wants height. */
export const LIBRARY_CEILING = 4.0

export const WALL_THICKNESS = 0.3
export const ENTRY_DOOR_HALF_WIDTH = 1.2
export const ENTRY_DOOR_HEIGHT = 2.4

// ── Stacks (four broad double-sided bookcase runs, north-south) ─────────────

export const LIBRARY_STACK_COUNT = 4
/** Shelf heights (Y of each shelf's top surface). Five shelves per face. */
export const LIBRARY_SHELF_YS: readonly number[] = [0.45, 1.05, 1.65, 2.25, 2.85]

/** How far the run centres sit inside the walls. */
export const STACK_USABLE_HALF_X = LIBRARY_HALF_X - 1.5 // 10.5
/** A run's footprint depth along X (the bookcase carcass). */
export const RUN_DEPTH_X = 0.9
/**
 * A run's Z extent. The north end stops early enough to create a real
 * research studio and cross-aisle rather than hiding loose furniture behind
 * a shelf tunnel.
 */
export const RUN_MIN_Z = -5.8
export const RUN_MAX_Z = 5.2
export const STACK_HEIGHT = 3.15

/** Local-space collision geometry shared by rendering and traversal proof. */
export const LIBRARY_STACK_COLLIDERS: readonly AABB[] = Array.from(
  { length: LIBRARY_STACK_COUNT },
  (_, index) => {
    const x = stackCenterX(index)
    return {
      minX: x - RUN_DEPTH_X / 2,
      maxX: x + RUN_DEPTH_X / 2,
      minY: 0,
      maxY: STACK_HEIGHT,
      minZ: RUN_MIN_Z,
      maxZ: RUN_MAX_Z,
    }
  },
)

export const BOOK_MARGIN_Z = 0.3
export const BOOK_PITCH_Z = 0.18
export const BOOK_WIDTH_Z = 0.14 // visible spine thickness
export const BOOK_HEIGHT = 0.24
export const BOOK_DEPTH_X = 0.18 // how far the book stands out from the carcass

/** Books that fit on one shelf face along a run. */
export const BOOKS_PER_SHELF = Math.floor(
  (RUN_MAX_Z - RUN_MIN_Z - 2 * BOOK_MARGIN_Z) / BOOK_PITCH_Z,
)

/** X centre of bookcase run `runIndex` (0-based, west→east). */
export function stackCenterX(runIndex: number): number {
  const pitch = (STACK_USABLE_HALF_X * 2) / LIBRARY_STACK_COUNT // 3.5
  return -STACK_USABLE_HALF_X + pitch * (runIndex + 0.5)
}

/** Clear aisle width between adjacent runs. */
export function aisleWidth(): number {
  return (STACK_USABLE_HALF_X * 2) / LIBRARY_STACK_COUNT - RUN_DEPTH_X
}

export interface BookSlot {
  runIndex: number
  /** -1 = west face, +1 = east face. */
  side: -1 | 1
  shelfIndex: number
  slotIndex: number
}

/**
 * The slot the n-th book fills. Fills a whole shelf face slot-by-slot, then the
 * next shelf up, then the far face, then the next run east. Returns null once
 * every run is full (that book is dropped rather than floating in space).
 */
export function bookSlot(index: number): BookSlot | null {
  const booksPerFace = BOOKS_PER_SHELF * LIBRARY_SHELF_YS.length
  const booksPerRun = booksPerFace * 2
  const runIndex = Math.floor(index / booksPerRun)
  if (runIndex >= LIBRARY_STACK_COUNT) return null
  const withinRun = index % booksPerRun
  const side: -1 | 1 = withinRun < booksPerFace ? -1 : 1
  const withinFace = withinRun % booksPerFace
  const shelfIndex = Math.floor(withinFace / BOOKS_PER_SHELF)
  const slotIndex = withinFace % BOOKS_PER_SHELF
  return { runIndex, side, shelfIndex, slotIndex }
}

/** Local-space centre of a book in the given slot. */
export function bookPosition(slot: BookSlot): [number, number, number] {
  const runX = stackCenterX(slot.runIndex)
  const x = runX + slot.side * (RUN_DEPTH_X / 2 + BOOK_DEPTH_X / 2)
  const y = LIBRARY_SHELF_YS[slot.shelfIndex] + BOOK_HEIGHT / 2
  const z = RUN_MIN_Z + BOOK_MARGIN_Z + (slot.slotIndex + 0.5) * BOOK_PITCH_Z
  return [x, y, z]
}

export interface BookPlacement {
  id: string
  title: string
  entityKind: string
  x: number
  y: number
  z: number
  side: -1 | 1
  runIndex: number
  shelfIndex: number
  slotIndex: number
}

/** Shelve every entry in order; entries past total capacity are dropped. */
export function shelveBooks(entries: readonly KnowledgeEntry[]): BookPlacement[] {
  const out: BookPlacement[] = []
  for (let i = 0; i < entries.length; i++) {
    const slot = bookSlot(i)
    if (!slot) break
    const [x, y, z] = bookPosition(slot)
    out.push({
      id: entries[i].id,
      title: entries[i].title,
      entityKind: entries[i].entityKind,
      x, y, z,
      side: slot.side,
      runIndex: slot.runIndex,
      shelfIndex: slot.shelfIndex,
      slotIndex: slot.slotIndex,
    })
  }
  return out
}

// ── Spine colour (deterministic hash of entity_kind) ────────────────────────

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v))
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) return [l, l, l]
  const hue2rgb = (p: number, q: number, t: number): number => {
    let tt = t
    if (tt < 0) tt += 1
    if (tt > 1) tt -= 1
    if (tt < 1 / 6) return p + (q - p) * 6 * tt
    if (tt < 1 / 2) return q
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
    return p
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  return [hue2rgb(p, q, h + 1 / 3), hue2rgb(p, q, h), hue2rgb(p, q, h - 1 / 3)]
}

/** Stable spine colour for an entity kind. Same kind → same colour. */
export function spineColor(entityKind: string): [number, number, number] {
  let hash = 0
  for (let i = 0; i < entityKind.length; i++) {
    hash = (hash * 31 + entityKind.charCodeAt(i)) >>> 0
  }
  const hue = (hash % 360) / 360
  const sat = 0.45 + ((hash >> 9) % 30) / 100 // 0.45..0.74
  const lit = 0.42 + ((hash >> 16) % 16) / 100 // 0.42..0.57
  return hslToRgb(hue, sat, lit)
}

// ── Relationship edges (the knowledge graph) ────────────────────────────────

export interface KnowledgeEdge {
  fromId: string
  toId: string
  type: string
  confidence: number
}

/** Bounded dynamic scene policy proven by the maximum-population browser gate. */
export const MAX_LIBRARY_RENDERED_SUBJECTS = 16
export const MAX_LIBRARY_RENDERED_RELATIONSHIPS = 8

/**
 * The graph edges to draw: every relationship whose BOTH ends are in the
 * loaded set. Self-edges and edges pointing at an unloaded id are dropped — a
 * relationship you can't anchor to a book on a shelf is not drawn.
 */
export function graphEdges(
  entries: readonly KnowledgeEntry[],
  loadedIds?: ReadonlySet<string>,
): KnowledgeEdge[] {
  const ids = loadedIds ?? new Set(entries.map((e) => e.id))
  const out: KnowledgeEdge[] = []
  for (const e of entries) {
    if (!ids.has(e.id)) continue
    for (const rel of e.relationships) {
      if (rel.to_id === e.id) continue
      if (!ids.has(rel.to_id)) continue
      out.push({ fromId: e.id, toId: rel.to_id, type: rel.type, confidence: rel.confidence })
    }
  }
  return out
}

/** The deterministic relationship subset materialized as world geometry. */
export function renderedGraphEdges(
  entries: readonly KnowledgeEntry[],
  loadedIds?: ReadonlySet<string>,
): KnowledgeEdge[] {
  return graphEdges(entries, loadedIds).slice(
    0,
    MAX_LIBRARY_RENDERED_RELATIONSHIPS,
  )
}

/** Thread opacity from an edge confidence (0..1 → 0.2..0.8). */
export function edgeOpacity(confidence: number): number {
  return 0.2 + 0.6 * clamp01(confidence)
}

// ── Map table ───────────────────────────────────────────────────────────────

export const MAP_TABLE = {
  x: 0,
  // Put the live knowledge graph in the entry commons. It is the room's useful
  // overview, so visitors should meet it before choosing a shelf aisle. Keep
  // it subordinate to the room: the smaller footprint preserves a generous
  // cross-aisle between the table and the stack field.
  z: 7.8,
  top: 1.0,
  halfX: 2.8,
  halfZ: 1.0,
} as const

export const LIBRARY_MAP_COLLIDER: Readonly<AABB> = {
  minX: MAP_TABLE.x - MAP_TABLE.halfX,
  maxX: MAP_TABLE.x + MAP_TABLE.halfX,
  minY: 0,
  maxY: MAP_TABLE.top,
  minZ: MAP_TABLE.z - MAP_TABLE.halfZ,
  maxZ: MAP_TABLE.z + MAP_TABLE.halfZ,
}


export interface MapPin {
  id: string
  x: number
  y: number
  z: number
}

/** Lay the loaded entries out as pins in an ellipse on the map-table top. */
export function mapPinPositions(entries: readonly KnowledgeEntry[]): MapPin[] {
  const n = entries.length
  const rx = MAP_TABLE.halfX - 0.6
  const rz = MAP_TABLE.halfZ - 0.4
  const out: MapPin[] = []
  for (let i = 0; i < n; i++) {
    const a = (i / Math.max(1, n)) * Math.PI * 2
    out.push({
      id: entries[i].id,
      x: MAP_TABLE.x + Math.cos(a) * rx,
      y: MAP_TABLE.top + 0.04,
      z: MAP_TABLE.z + Math.sin(a) * rz,
    })
  }
  return out
}

// ── Rendering system ────────────────────────────────────────────────────────

export const POLL_INTERVAL_MS = 20_000
/** How many of the nearest books get a textured spine label. */
export const MAX_LABELLED_BOOKS = 14
export const LABEL_ATLAS_COLS = 8
export const LABEL_CELL_W = 256
export const LABEL_CELL_H = 48
export const INTERACT_DIST = 2.5
